import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import type { NetworkTier, PersonNetwork } from '../../../types/network'
import type { NetworkTime } from './NetworkList'
import { spread } from './spread'

const W = 820
const H = 560
const CX = W / 2
const CY = H / 2
const COMPANY_RING = { rx: 175, ry: 125 }
const PERSON_RING = { rx: 300, ry: 220 }
const MAX_PEOPLE = 28

const EDGE_WIDTH: Record<NetworkTier, number> = {
  close: 2.6,
  working: 1.7,
  peripheral: 1,
}

interface Node {
  id: string
  kind: 'person' | 'company'
  label: string
  x: number
  y: number
  to: string
  current: boolean
  collapsed?: boolean
  tier?: NetworkTier
}

interface Edge {
  a: string
  b: string
  width: number
  current: boolean
}

function at(angle: number, ring: { rx: number; ry: number }) {
  return {
    x: CX + Math.cos(angle) * ring.rx,
    y: CY + Math.sin(angle) * ring.ry,
  }
}

function short(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s
}

/**
 * The network as a picture: the person in the middle, their companies on
 * the inner ring, connections on the outer ring next to the companies they
 * share. Thicker line = closer tie; dashed = no longer. Same data and the
 * same Nu/Alle år filter as the list.
 */
const NetworkMap: React.FC<{ network: PersonNetwork; time: NetworkTime }> = ({
  network,
  time,
}) => {
  const navigate = useNavigate()
  const [hover, setHover] = useState<string | null>(null)

  const { nodes, edges, hiddenPeople } = useMemo(() => {
    const people = network.connections
      .filter(c => time === 'all' || c.current)
      .slice()
      .sort((a, b) => b.score - a.score)
    const shownPeople = people.slice(0, MAX_PEOPLE)

    // »Nu« draws only ties that still hold; »Alle år« every shared company.
    const links = (c: (typeof people)[number]) =>
      time === 'all' ? c.companies : c.companies.filter(s => s.current)
    const usedCompanies = new Set(
      shownPeople.flatMap(c => links(c).map(s => s.cvr))
    )
    const companies = network.companies.filter(
      co => usedCompanies.has(co.cvr) || (time === 'all' ? true : co.active)
    )

    const nodes: Node[] = []
    const edges: Edge[] = []
    const angleOf = new Map<string, number>()
    companies.forEach((co, i) => {
      const angle =
        (i / Math.max(1, companies.length)) * Math.PI * 2 - Math.PI / 2
      angleOf.set(co.cvr, angle)
      nodes.push({
        id: `c${co.cvr}`,
        kind: 'company',
        label: co.name,
        ...at(angle, COMPANY_RING),
        to: `/company/${co.cvr}`,
        current: co.active,
        collapsed: co.collapsed,
      })
      edges.push({
        a: 'subject',
        b: `c${co.cvr}`,
        width: 1,
        current: co.active,
      })
    })

    // Each person sits by the mean angle of their companies, then is pushed
    // just far enough from its neighbours that labels do not collide, and
    // each crowd is re-centred on its company.
    const wanted = shownPeople.map(c => {
      let sx = 0
      let sy = 0
      for (const s of links(c)) {
        const a = angleOf.get(s.cvr)
        if (a === undefined) continue
        sx += Math.cos(a) * (s.score + 0.1)
        sy += Math.sin(a) * (s.score + 0.1)
      }
      return { c, angle: Math.atan2(sy, sx) }
    })
    wanted.sort((a, b) => a.angle - b.angle)
    const angles = spread(
      wanted.map(w => w.angle),
      Math.min((Math.PI * 2) / Math.max(1, wanted.length), 0.3)
    )
    wanted.forEach(({ c }, i) => {
      const angle = angles[i]
      nodes.push({
        id: `p${c.enhedsNummer}`,
        kind: 'person',
        label: c.navn,
        ...at(angle, PERSON_RING),
        to: `/person/${c.enhedsNummer}`,
        current: c.current,
        tier: c.tier,
      })
      for (const s of links(c)) {
        if (!angleOf.has(s.cvr)) continue
        edges.push({
          a: `c${s.cvr}`,
          b: `p${c.enhedsNummer}`,
          width: EDGE_WIDTH[c.tier],
          current: s.current,
        })
      }
    })
    return { nodes, edges, hiddenPeople: people.length - shownPeople.length }
  }, [network, time])

  const pos = useMemo(() => {
    const m = new Map<string, { x: number; y: number }>([
      ['subject', { x: CX, y: CY }],
    ])
    for (const n of nodes) m.set(n.id, n)
    return m
  }, [nodes])

  const near = useMemo(() => {
    if (!hover) return null
    const s = new Set<string>([hover])
    for (const e of edges) {
      if (e.a === hover) s.add(e.b)
      if (e.b === hover) s.add(e.a)
    }
    return s
  }, [hover, edges])

  const dim = (id: string) => (near && !near.has(id) ? 0.18 : 1)
  const open = (to: string) => navigate(to)

  if (nodes.length === 0) {
    return (
      <p className="rounded-[10px] border border-rule bg-paper px-5 py-6 text-sm text-ink-500">
        Intet at tegne for den valgte periode.
      </p>
    )
  }

  return (
    <figure
      className="overflow-hidden rounded-[10px] border border-rule bg-canvas-graph"
      data-testid="network-map"
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full select-none"
        role="group"
        aria-label={`Netværk for ${network.person.navn}: ${nodes.filter(n => n.kind === 'company').length} selskaber og ${nodes.filter(n => n.kind === 'person').length} personer`}
      >
        <g>
          {edges.map((e, i) => {
            const a = pos.get(e.a)!
            const b = pos.get(e.b)!
            const lit = near ? near.has(e.a) && near.has(e.b) : true
            return (
              <line
                key={i}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={lit && near ? '#12151A' : '#9AA2AD'}
                strokeWidth={e.width}
                strokeDasharray={e.current ? undefined : '4 4'}
                opacity={lit ? 0.9 : 0.12}
              />
            )
          })}
        </g>
        <g>
          <circle cx={CX} cy={CY} r={30} fill="#12151A" />
          <text
            x={CX}
            y={CY + 48}
            textAnchor="middle"
            className="fill-ink text-[14px] font-bold"
          >
            {short(network.person.navn, 28)}
          </text>
          <text
            x={CX}
            y={CY + 5}
            textAnchor="middle"
            className="fill-paper text-[13px] font-bold"
          >
            {network.person.navn
              .split(/\s+/)
              .filter(Boolean)
              .map((p, i, all) => (i === 0 || i === all.length - 1 ? p[0] : ''))
              .join('')
              .toUpperCase()}
          </text>
        </g>
        {nodes.map(n => {
          const left = n.x < CX - 4
          const labelX = n.kind === 'person' ? n.x + (left ? -12 : 12) : n.x
          const labelY = n.kind === 'person' ? n.y + 4 : n.y + 22
          return (
            <g
              key={n.id}
              role="link"
              tabIndex={0}
              aria-label={`${n.label}${n.kind === 'company' ? ' (selskab)' : ''}`}
              opacity={dim(n.id)}
              className="cursor-pointer outline-none [&:focus-visible>.ring]:opacity-100"
              onMouseEnter={() => setHover(n.id)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(n.id)}
              onBlur={() => setHover(null)}
              onClick={() => open(n.to)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  open(n.to)
                }
              }}
            >
              <title>{n.label}</title>
              {n.kind === 'company' ? (
                <>
                  <rect
                    className="ring opacity-0"
                    x={n.x - 11}
                    y={n.y - 11}
                    width={22}
                    height={22}
                    rx={5}
                    fill="none"
                    stroke="#2B50D6"
                    strokeWidth={2}
                  />
                  <rect
                    x={n.x - 7}
                    y={n.y - 7}
                    width={14}
                    height={14}
                    rx={3}
                    fill={n.collapsed ? '#FCEEEA' : '#FFFFFF'}
                    stroke={
                      n.collapsed
                        ? '#C93A22'
                        : n.current
                          ? '#12151A'
                          : '#9AA2AD'
                    }
                    strokeWidth={1.5}
                    strokeDasharray={n.current ? undefined : '3 2'}
                  />
                </>
              ) : (
                <>
                  <circle
                    className="ring opacity-0"
                    cx={n.x}
                    cy={n.y}
                    r={10}
                    fill="none"
                    stroke="#2B50D6"
                    strokeWidth={2}
                  />
                  <circle
                    cx={n.x}
                    cy={n.y}
                    r={n.tier === 'close' ? 6.5 : n.tier === 'working' ? 5 : 4}
                    fill={n.current ? '#12151A' : '#FFFFFF'}
                    stroke="#12151A"
                    strokeWidth={1.5}
                  />
                </>
              )}
              <text
                x={labelX}
                y={labelY}
                textAnchor={
                  n.kind === 'person' ? (left ? 'end' : 'start') : 'middle'
                }
                className={`text-[12px] ${n.kind === 'person' ? 'fill-ink font-semibold' : 'fill-ink-500'}`}
                paintOrder="stroke"
                stroke="#FBFBFC"
                strokeWidth={4}
              >
                {short(n.label, n.kind === 'person' ? 24 : 20)}
              </text>
            </g>
          )
        })}
      </svg>
      <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-rule px-4 py-2.5 text-[12px] text-ink-500">
        <span className="inline-flex items-center gap-1.5">
          <svg width="22" height="6" aria-hidden>
            <line
              x1="0"
              y1="3"
              x2="22"
              y2="3"
              stroke="#12151A"
              strokeWidth="2.6"
            />
          </svg>
          Tæt partner
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="22" height="6" aria-hidden>
            <line
              x1="0"
              y1="3"
              x2="22"
              y2="3"
              stroke="#9AA2AD"
              strokeWidth="1.5"
              strokeDasharray="4 4"
            />
          </svg>
          Tidligere
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="12" height="12" aria-hidden>
            <rect
              x="1"
              y="1"
              width="10"
              height="10"
              rx="2"
              fill="#FCEEEA"
              stroke="#C93A22"
              strokeWidth="1.5"
            />
          </svg>
          Konkurs/tvangsopløst
        </span>
        {hiddenPeople > 0 && (
          <span>
            De {hiddenPeople} svageste forbindelser er ikke tegnet. Se dem i
            listen.
          </span>
        )}
      </figcaption>
    </figure>
  )
}

export default NetworkMap
